/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import {
	PlanAccessLevel,
	ProjectMember,
	Ticket,
	TicketFields,
	TicketType,
	isProjectTypeDisabled,
} from '@touchplan/common';
import { PlanAccess } from '../app-data/models/PlanAccess';
import { ProjectLegacy } from '../app-data/models/Project';

/**
 * Determines if access to the plan is restricted based on the provided project, plan access,
 * and authenticated project member.
 *
 * @param {ProjectLegacy} project - The project of the evaluated plan.
 * @param {PlanAccess} planAccess - The "planAccess" information of the plan.
 * @param {ProjectMember} authedProjectMember - The authenticated project member. Deprecated paramater
 * mostly just used to block while loading data at this point.
 *
 * @returns {boolean} - Returns `true` if either the project or authenticated project member
 * is not defined. Otherwise, it checks the following conditions:
 * - The project type is disabled.
 * - The plan access level is either 'locked' or 'archived'.
 */
export function hasRestrictedAccess(
	project: ProjectLegacy | null,
	planAccess: PlanAccess,
	authedProjectMember: ProjectMember | null
): boolean {
	if (!project || !authedProjectMember) {
		return true;
	}
	return (
		isProjectTypeDisabled(project.projectType) ||
		planAccess?.view?.accessLevel === PlanAccessLevel.locked ||
		planAccess?.view?.accessLevel === PlanAccessLevel.archived
	);
}

/**
 * Determines if the authenticated project member has access to the provided ticket.
 *
 * @param {TicketFields} ticket - The ticket for which access is being evaluated.
 * @param {ProjectMember} authedProjectMember - The authenticated project member.
 *
 * @returns {boolean} - Returns `false` if the authenticated project member is not defined.
 * Otherwise, for constraint-type tickets, it checks whether the member is a project admin or
 * has access through the constraint-specific access rules. For other ticket types, it checks
 * if the member is an admin or has access based on the member's role.
 */
export function hasAccessToTicket(ticket: TicketFields, authedProjectMember: ProjectMember | null) {
	if (!authedProjectMember || !ticket) {
		return false;
	}
	const ticketFields = ticketFieldsUnwrap(ticket);

	if (ticketFields.type === TicketType.Constraint) {
		return (
			isProjectMemberAdmin(authedProjectMember) || constraintHasAccess(ticketFields, authedProjectMember)
		);
	}
	return isProjectMemberAdmin(authedProjectMember) || roleHasAccess(ticketFields, authedProjectMember);
}

/**
 * Checks if the authenticated project member has an admin access level.
 *
 * @param {ProjectMember | null} authedProjectMember - The authenticated project member.
 *
 * @returns {boolean} - Returns `true` if the authenticated project member has an access
 * level of 'Admin'. Otherwise, returns `false`.
 */
export function isProjectMemberAdmin(authedProjectMember: ProjectMember | null) {
	return authedProjectMember?.view?.accessLevel === 'Admin';
}

/**
 * Determines if the authenticated project member has access to the provided constraint ticket.
 *
 * @param {TicketFields} ticket - The constraint ticket for which access is being evaluated.
 * @param {ProjectMember | null} authedProjectMember - The authenticated project member.
 *
 * @returns {boolean} - Returns `false` if the authenticated project member is not defined.
 * Otherwise, it checks if the member has access by evaluating their primary and secondary roles
 * against key roles in the ticket. Specifically, access is granted if the member is:
 * - The assigned constraint member.
 * - The creator of the constraint.
 * - The responsible or live-assigned project member for the constraint.
 *
 * The roles of the authenticated member are combined into one object if secondary roles exist,
 * and they are checked against the following ticket roles: `responsibleProjectMemberRoleId`,
 * `assignedProjectMemberRoleId`, `requestedByProjectMemberRoleId`, and `liveAssignedProjectMemberId`.
 */
export function constraintHasAccess(ticket: TicketFields, authedProjectMember: ProjectMember | null) {
	if (!authedProjectMember) {
		return false;
	}
	// Vi's notes: One has access to constraints when they assigned the constraint or is an assignee of the constraint.
	// combine authMember roleId and secondaryRoles into 1 object if secondary roles exists
	const authMemberRoles = {
		[authedProjectMember.view.roleId]: authedProjectMember.view.roleId,
		...authedProjectMember.view.secondaryRoles,
	};

	// authMemberRoles contains both primary role and secondary roles
	// user has access if they are the assigned constraint member, the one creating the constraint, or responsible for
	// https://mocasystems.atlassian.net/wiki/spaces/DI/pages/2394521604/Constraint+Role+Logic
	return (
		!!authMemberRoles[ticket.responsibleProjectMemberRoleId] ||
		!!authMemberRoles[ticket.assignedProjectMemberRoleId] ||
		!!authMemberRoles[ticket.requestedByProjectMemberRoleId] ||
		!!authMemberRoles[ticket.liveAssignedProjectMemberId]
	);
}

/**
 * Determines if the authenticated project member has access to the provided ticket based on their role.
 *
 * @param {TicketFields} ticket - The ticket for which access is being evaluated.
 * @param {ProjectMember} authedProjectMember - The authenticated project member.
 *
 * @returns {boolean} - Returns `true` if the authenticated project member's primary or
 * secondary roles match the ticket's role. Otherwise, returns `false`. The ticket's role is
 * determined by either the `liveRole` or `roleId`.
 */
export function roleHasAccess(ticket: TicketFields, authedProjectMember: ProjectMember): boolean {
	const ticketRoleId = ticket.liveRole?.view.roleId || ticket.roleId;
	const { roleId, secondaryRoles } = authedProjectMember.view;
	// check if the member's primary or secondary roles match the ticket role
	return ticketRoleId === roleId || !!secondaryRoles?.[ticketRoleId];
}

/** angularfire1 style ticket data */
function isLegacyTicket(x: unknown): x is { data: TicketFields } {
	return !!(x as any).data;
}
function isTicket(x: unknown): x is Ticket {
	return !!(x as Ticket).view;
}

function ticketFieldsUnwrap(ticket: Ticket | TicketFields | { data: TicketFields }): TicketFields {
	//changed this to duck-typing
	// it is possible that the ticket is null. See TP-524
	if (!ticket) {
		return ticket; //pass through the falsy
	} else if (isTicket(ticket)) {
		return ticket.view;
	} else if (isLegacyTicket(ticket)) {
		return ticket.data;
	} else {
		return ticket;
	}
}
